import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDataChannel, useLocalParticipant, useRemoteParticipants } from '@livekit/components-react';
import { ParticipantEvent, Track } from 'livekit-client';
import { useWebAudioContext } from '@shared/context/WebAudio';
import { getJukeboxVolumes, useJukeboxVolumeStore } from '../model/jukebox-volume.store';
import {
  JUKEBOX_SYNC_TOPIC,
  SYNC_HEARTBEAT_MS,
  isSyncFresh,
  computeClockOffsetMs,
  computeScheduleDelayMs,
  decodeSyncMessage,
} from '../lib/jukebox-sync';
import { heardBeatCalibrator } from '../lib/heard-beat-calibrator';
import { syncMixBus } from '../lib/sync-mix-bus';

/** Вид синхронного аудио: музыка из плеера или метроном. */
export const JUKEBOX_KINDS = {
  music: 'music',
  metronome: 'metronome',
};

/**
 * Имя постоянного микс-трека. Микрофон и музыка/метроном ведущего
 * микшируются в ОДНУ шину, опубликованную один раз на сессию: слушатель
 * получает «соединённый» трек, в котором голос и музыка выровнены
 * sample-точно (выравнивание в аудиочасах ведущего), а старт/стоп музыки
 * не трогает публикацию — бесшовно, без ре-аттача у слушателя.
 *
 * ВАЖНО (AEC): микрофон захватывается LiveKit со включённым эхоподавлением
 * по умолчанию — оно подавляет акустическую утечку метронома/музыки из
 * колонок ведущего обратно в микрофон. При смене constraint'ов захвата
 * (экран настроек) эхоподавление НЕ отключать, иначе слушатель услышит
 * музыку дважды (из микса и через микрофон).
 */
export const SYNC_MIX_TRACK_NAME = 'sync-mix';

/** Свежесть по умолчанию: isSyncFresh(raw, Date.now(), SYNC_STALE_MS). */
const SYNC_STALE_MS = 20000;

/** Ducking: музыка приглушается, пока ведущий говорит (envelope follower голоса). */
const DUCK_CHECK_MS = 30; // период детектора
const DUCK_THRESHOLD_RMS = 0.03; // RMS микрофона выше — голос есть
const DUCK_LEVEL = 0.6; // −4.4 дБ во время речи
const DUCK_ATTACK_TC = 0.05; // с — быстро вниз
const DUCK_RELEASE_TC = 0.3; // с — медленно обратно

export const AudioControlsContext = createContext({
  jukeBoxParticipant: null,
  jukeBoxKind: JUKEBOX_KINDS.music,
  amIPlayingJukeBox: false,
  someoneElsePlayingJukeBox: false,
  micEnabled: true,
  remoteSync: null,
  playJukeBox: () => Promise.resolve(),
  stopJukeBox: () => Promise.resolve(),
});

export const AudioControlsProvider = ({ children }) => {
  const audioContext = useWebAudioContext();
  const [ audio, setAudio ] = useState(null);
  const [ isPlayingLocal, setIsPlayingLocal ] = useState(false);

  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const { musicVolume, metronomeVolume } = useJukeboxVolumeStore();

  // --- data channel ---
  const [ remoteSyncRaw, setRemoteSyncRaw ] = useState(null); // { ...msg, from, receivedAt }
  const handleSyncMessageRef = useRef(() => {});
  const { send } = useDataChannel(
    JUKEBOX_SYNC_TOPIC,
    useCallback((msg) => {
      if (msg.from?.isLocal) return;
      const data = decodeSyncMessage(msg.payload);
      if (data) handleSyncMessageRef.current(data, msg.from?.identity);
    }, [])
  );

  const sendSync = useCallback((payload, destinationIdentities) => {
    try {
      send(new TextEncoder().encode(JSON.stringify(payload)), {
        reliable: true,
        ...(destinationIdentities ? { destinationIdentities } : {}),
      }).catch(() => {
        // data channel ещё не открыт или уже закрыт — не критично:
        // звук от этого не зависит, слушатель просто не увидит визуал
      });
    } catch {
      // синхронизация визуала опциональна
    }
  }, [send]);

  // --- часы/сеть ---
  const clockOffsetsRef = useRef(new Map()); // identity -> θ (мс: часы identity минус мои)
  const rttByIdentityRef = useRef(new Map());
  const pendingPingsRef = useRef(new Map()); // pingId -> t0
  const scheduleDelayMsRef = useRef(250);
  const pingIntervalRef = useRef(null);
  const heartbeatRef = useRef(null);
  const lastStartPayloadRef = useRef(null);

  // --- микс-шина ---
  const mixDest = useRef(null);
  const mixLimiter = useRef(null); // защита входа кодека от клиппинга голос+музыка
  const voiceAnalyser = useRef(null); // детектор речи для ducking'а музыки
  const voiceTapGain = useRef(null); // нулевой gain, чтобы анализатор «пуллился»
  const voiceBuf = useRef(null);
  const micSource = useRef(null);
  const voiceVolumeGain = useRef(null); // громкость микрофона в шине (настройки конференции)
  const mixPublication = useRef(null);
  const savedMic = useRef({ track: null, enabled: true });
  const micPatchRef = useRef(null);
  const busQueueRef = useRef(Promise.resolve());

  // --- музыка ---
  const musicBusGain = useRef(null);
  const musicDuckGain = useRef(null); // ducking: голос приглушает музыку
  const duckTimer = useRef(null);
  const localGain = useRef(null);
  const sourceNode = useRef(null);
  const currentKind = useRef(null);
  const startGeneration = useRef(0);
  const startSerial = useRef(0);

  const sendPing = useCallback(() => {
    const pingId = Math.random().toString(36).slice(2);
    pendingPingsRef.current.set(pingId, Date.now());
    sendSync({ type: JUKEBOX_SYNC_TOPIC, action: 'ping', pingId, t0: Date.now() });
  }, [sendSync]);

  const stopHeartbeatInternal = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const stopDuckingInternal = useCallback(() => {
    if (duckTimer.current) {
      clearInterval(duckTimer.current);
      duckTimer.current = null;
    }
  }, []);

  /**
   * Петля ducking'а: пока ведущий говорит, музыка приглушается до DUCK_LEVEL.
   * Envelope follower по RMS микрофона: вниз быстро (attack), обратно медленно
   * (release) — слышимых «пампов» нет. Работает только пока играет музыка.
   */
  const startDuckingInternal = useCallback(() => {
    stopDuckingInternal();
    const analyser = voiceAnalyser.current;
    const duck = musicDuckGain.current;
    if (!analyser || !duck) return;
    duckTimer.current = setInterval(() => {
      if (!voiceBuf.current) return;
      analyser.getFloatTimeDomainData(voiceBuf.current);
      let sum = 0;
      for (let i = 0; i < voiceBuf.current.length; i++) {
        sum += voiceBuf.current[i] * voiceBuf.current[i];
      }
      const rms = Math.sqrt(sum / voiceBuf.current.length);
      const target = rms > DUCK_THRESHOLD_RMS ? DUCK_LEVEL : 1;
      const timeConstant = target < duck.gain.value ? DUCK_ATTACK_TC : DUCK_RELEASE_TC;
      duck.gain.setTargetAtTime(target, audioContext.currentTime, timeConstant);
    }, DUCK_CHECK_MS);
  }, [audioContext, stopDuckingInternal]);

  /** Финальная разборка нод музыки — после отыгранного фейд-аута. */
  const teardownMusicNodes = useCallback((nodes) => {
    if (nodes.monitor) {
      nodes.monitor.disconnect();
    }
    if (nodes.duck) {
      nodes.duck.disconnect();
    }
    if (nodes.bus) {
      nodes.bus.disconnect();
    }
    if (nodes.source) {
      nodes.source.disconnect();
    }
  }, []);

  /**
   * Остановка музыки: фейд-аут ~10 мс против щелчка на границе, источник
   * глушим чуть позже фейда, ноды разбираем после отыгрыша. Публикация
   * шины не трогается — стоп музыки бесшовный.
   */
  const cleanupMusicInternal = useCallback(() => {
    stopDuckingInternal();
    const nodes = {
      source: sourceNode.current,
      bus: musicBusGain.current,
      duck: musicDuckGain.current,
      monitor: localGain.current,
    };
    sourceNode.current = null;
    musicBusGain.current = null;
    musicDuckGain.current = null;
    localGain.current = null;
    currentKind.current = null;

    if (!nodes.source) return;
    const now = audioContext.currentTime;
    try {
      // фейд-аут против щелчка на границе
      nodes.bus?.gain.setValueAtTime(nodes.bus.gain.value, now);
      nodes.bus?.gain.linearRampToValueAtTime(0, now + 0.01);
      nodes.source.stop(now + 0.02);
    } catch (e) {
      try {
        nodes.source.stop();
      } catch {
        // уже остановлен
      }
    }
    setTimeout(() => teardownMusicNodes(nodes), 60);
  }, [audioContext, stopDuckingInternal, teardownMusicNodes]);

  const handleSyncMessage = useCallback((data, fromIdentity) => {
    if (!fromIdentity) return;
    if (data.action === 'ping') {
      // отвечаем только пингеру; t1/t2 — в НАШИХ часах
      const now = Date.now();
      sendSync(
        { type: JUKEBOX_SYNC_TOPIC, action: 'pong', pingId: data.pingId, t0: data.t0, t1: now, t2: now },
        [fromIdentity]
      );
      return;
    }
    if (data.action === 'pong') {
      const t0 = pendingPingsRef.current.get(data.pingId);
      pendingPingsRef.current.delete(data.pingId);
      if (t0 === undefined) return;
      // θ = смещение часов резондента (обычно ведущего) относительно нас
      const { offsetMs, rttMs } = computeClockOffsetMs({ t0, t1: data.t1, t2: data.t2, t3: Date.now() });
      const prevOffset = clockOffsetsRef.current.get(fromIdentity);
      clockOffsetsRef.current.set(
        fromIdentity,
        prevOffset === undefined ? offsetMs : prevOffset * 0.7 + offsetMs * 0.3
      );
      rttByIdentityRef.current.set(fromIdentity, rttMs);
      // адаптивный запас старта: сообщение о старте должно обогнать звук
      const maxRtt = Math.max(0, ...rttByIdentityRef.current.values());
      scheduleDelayMsRef.current = computeScheduleDelayMs(maxRtt);
      return;
    }
    // start/stop — состояние для визуала слушателя
    setRemoteSyncRaw({ ...data, from: fromIdentity, receivedAt: Date.now() });
  }, [sendSync]);
  handleSyncMessageRef.current = handleSyncMessage;

  const startHeartbeat = useCallback(() => {
    stopHeartbeatInternal();
    heartbeatRef.current = setInterval(() => {
      if (lastStartPayloadRef.current) {
        sendSync(lastStartPayloadRef.current);
      }
    }, SYNC_HEARTBEAT_MS);
  }, [sendSync, stopHeartbeatInternal]);

  // --- сериализация операций с шиной (install/teardown/repatch не пересекаются) ---
  const enqueueBus = useCallback((fn) => {
    const run = busQueueRef.current.then(fn, fn);
    busQueueRef.current = run.then(() => {}, () => {});
    return run;
  }, []);

  const unpatchMicToggle = useCallback(() => {
    if (micPatchRef.current) {
      localParticipant.setMicrophoneEnabled = micPatchRef.current.original;
      micPatchRef.current = null;
    }
  }, [localParticipant]);

  /** Снимает микс и возвращает чистый микрофон в эфир. */
  const teardownBusInternal = useCallback(async ({ restoreMic = true } = {}) => {
    stopHeartbeatInternal();
    startGeneration.current += 1;
    setIsPlayingLocal(false);
    cleanupMusicInternal();

    // Интерфейс шины возвращаем на заглушки — внешние системы (EQ)
    // не должны дёргать отсутствующую шину
    syncMixBus.isActive = () => false;
    syncMixBus.setVoiceTrack = () => {};
    syncMixBus.setVoiceVolume = () => {};
    syncMixBus.getRawVoiceTrack = () => null;

    const pub = mixPublication.current;
    mixPublication.current = null;
    if (pub?.track) {
      await localParticipant.unpublishTrack(pub.track, true).catch(() => {});
    }
    if (micSource.current) {
      micSource.current.disconnect();
      micSource.current = null;
    }
    if (voiceVolumeGain.current) {
      voiceVolumeGain.current.disconnect();
      voiceVolumeGain.current = null;
    }
    if (voiceAnalyser.current) {
      voiceAnalyser.current.disconnect();
      voiceAnalyser.current = null;
      voiceBuf.current = null;
    }
    if (voiceTapGain.current) {
      voiceTapGain.current.disconnect();
      voiceTapGain.current = null;
    }
    if (mixLimiter.current) {
      mixLimiter.current.disconnect();
      mixLimiter.current = null;
    }
    if (mixDest.current) {
      mixDest.current.disconnect();
      mixDest.current = null;
    }
    const micTrack = savedMic.current.track;
    const micEnabled = savedMic.current.enabled;
    savedMic.current = { track: null, enabled: true };
    if (restoreMic && micTrack && micEnabled && !localParticipant.getTrackPublication(Track.Source.Microphone)) {
      await localParticipant.publishTrack(micTrack, { source: Track.Source.Microphone }).catch(() => {});
    } else if (micTrack && (!restoreMic || !micEnabled)) {
      micTrack.stop();
    }
  }, [cleanupMusicInternal, localParticipant, stopHeartbeatInternal]);

  const teardownBus = useCallback(() => enqueueBus(teardownBusInternal), [enqueueBus, teardownBusInternal]);

  /**
   * Перехват setMicrophoneEnabled на время жизни шины.
   * Обычный тоггл (без options) делегируем LiveKit — он мьютит сам микс
   * (у него source: Microphone). Вызов С options (смена устройства/constraint'ов
   * из настроек конференции) нативно проигнорировал бы новое устройство —
   * поэтому пересобираем шину на новом захвате.
   */
  const patchMicToggle = useCallback(() => {
    if (micPatchRef.current) return;
    const lp = localParticipant;
    const original = lp.setMicrophoneEnabled.bind(lp);
    micPatchRef.current = { original };
    lp.setMicrophoneEnabled = async (enabled, options, publishOptions) => {
      if (!options) {
        return original(enabled, options, publishOptions);
      }
      await enqueueBus(async () => {
        // Старый микрофон НЕ восстанавливаем: иначе original() увидит
        // существующую публикацию и просто сделает unmute — новое устройство
        // не применится. Глушим старый захват, original() пересоздаст трек.
        await teardownBusInternal({ restoreMic: false });
        unpatchMicToggle();
        await original(true, options, publishOptions);
        // LocalTrackPublished (подписан ниже на весь жизненный цикл) поставит шину заново
      });
      return lp.getTrackPublication(Track.Source.Microphone);
    };
  }, [enqueueBus, localParticipant, teardownBusInternal, unpatchMicToggle]);

  /** Ставит постоянную шину: микрофон ведущего → микс → один трек. */
  const tryInstallBusInternal = useCallback(async () => {
    if (mixPublication.current) return;
    // micUnpublished: микрофон уже снят с публикации — если дальше упадём,
    // его надо вернуть в эфир, иначе собеседники потеряют и голос
    let micUnpublished = false;
    try {
      await audioContext.resume().catch(() => {});

      const micPub = localParticipant.getTrackPublication(Track.Source.Microphone);
      const micTrack = micPub?.track ?? null;

      savedMic.current = { track: micTrack, enabled: micTrack ? !micPub.isMuted : true };
      if (!mixDest.current) {
        mixDest.current = audioContext.createMediaStreamDestination();
      }
      if (!mixLimiter.current) {
        mixLimiter.current = audioContext.createDynamicsCompressor();
        mixLimiter.current.threshold.value = -3; // дБFS
        mixLimiter.current.knee.value = 0;
        mixLimiter.current.ratio.value = 12;
        mixLimiter.current.attack.value = 0.003;
        mixLimiter.current.release.value = 0.25;
      }
      mixLimiter.current.connect(mixDest.current);
      // Детектор речи: анализатор «пуллится» через нулевой gain в destination,
      // на звук это не влияет; к нему подключается любой голосовой источник
      // (сырой микрофон, выход EQ и т.п.) — см. setVoiceTrack ниже
      voiceAnalyser.current = audioContext.createAnalyser();
      voiceAnalyser.current.fftSize = 1024;
      voiceBuf.current = new Float32Array(1024);
      voiceTapGain.current = audioContext.createGain();
      voiceTapGain.current.gain.value = 0;
      voiceAnalyser.current.connect(voiceTapGain.current);
      voiceTapGain.current.connect(audioContext.destination);
      if (micTrack) {
        if (!voiceVolumeGain.current) {
          voiceVolumeGain.current = audioContext.createGain();
        }
        voiceVolumeGain.current.gain.value = 1;
        voiceVolumeGain.current.connect(mixLimiter.current);
        // анализатор речи ПОСЛЕ гейна громкости: дакинг реагирует на реально
        // уходящий в эфир голос
        voiceVolumeGain.current.connect(voiceAnalyser.current);
        micSource.current = audioContext.createMediaStreamSource(
          new MediaStream([micTrack.mediaStreamTrack])
        );
        micSource.current.connect(voiceVolumeGain.current);
        await localParticipant.unpublishTrack(micTrack, false); // захват НЕ останавливаем
        micUnpublished = true;
      }
      const mixTrack = mixDest.current.stream.getAudioTracks()[0];
      // Трек несёт музыку: 'music' переключает Chromium на Opus application
      // AUDIO (лучше держит верхнюю полосу), dtx:false убирает провалы
      // передачи в тихих местах музыки
      mixTrack.contentHint = 'music';
      mixPublication.current = await localParticipant.publishTrack(mixTrack, {
        name: SYNC_MIX_TRACK_NAME,
        source: Track.Source.Microphone,
        dtx: false,
      });
      patchMicToggle();
      // Публикуем интерфейс шины для внешних систем обработки голоса (EQ и т.п.)
      syncMixBus.isActive = () => !!mixPublication.current;
      syncMixBus.getRawVoiceTrack = () => savedMic.current.track;
      syncMixBus.setVoiceTrack = (mediaStreamTrack) => {
        if (!mixDest.current || !mediaStreamTrack) return;
        if (micSource.current) {
          micSource.current.disconnect();
        }
        micSource.current = audioContext.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
        if (voiceVolumeGain.current) {
          micSource.current.connect(voiceVolumeGain.current);
        } else {
          micSource.current.connect(mixLimiter.current ?? mixDest.current);
          if (voiceAnalyser.current) {
            micSource.current.connect(voiceAnalyser.current);
          }
        }
      };
      // Громкость микрофона — гейном в ЖИВОМ контексте шины (не в EQ-цепочке
      // на клоне трека): надёжно во всех браузерах, применяется мгновенно.
      // Выставляется из настроек конференции (use-conference-settings-audio).
      syncMixBus.setVoiceVolume = (volume01) => {
        if (!voiceVolumeGain.current) return;
        voiceVolumeGain.current.gain.value = Math.min(1, Math.max(0, volume01));
      };
    } catch (error) {
      if (micUnpublished && savedMic.current.track && savedMic.current.enabled) {
        await localParticipant
          .publishTrack(savedMic.current.track, { source: Track.Source.Microphone })
          .catch(() => {});
      }
      console.error('[sync-audio] установка микс-шины не удалась:', error);
      throw error;
    }
  }, [audioContext, localParticipant, patchMicToggle]);

  const tryInstallBus = useCallback(() => enqueueBus(tryInstallBusInternal), [enqueueBus, tryInstallBusInternal]);

  const ensureBus = useCallback(async () => {
    if (mixPublication.current) return;
    await tryInstallBus();
  }, [tryInstallBus]);

  // Монтирование шины: сразу (микрофон обычно уже опубликован) и при поздней публикации
  useEffect(() => {
    tryInstallBus();
    const onLocalTrackPublished = (publication) => {
      const source = publication?.track?.source ?? publication?.source;
      if (source !== Track.Source.Microphone) return;
      enqueueBus(async () => {
        const mixPub = mixPublication.current;
        // Микс мог быть снят внешне (реконнект, протухший ref) — тогда шину
        // надо поставить заново, а не подсаживать микрофон в мёртвую шину
        const mixAlive =
          !!mixPub?.trackSid &&
          (localParticipant.trackPublications?.has?.(mixPub.trackSid) ?? true);
        if (!mixPub || !mixAlive) {
          await tryInstallBusInternal();
          return;
        }
        // Собственная публикация микса тоже идёт с source: Microphone — её
        // НЕЛЬЗЯ «подсаживать в шину»: иначе микс собственным же событием
        // снимается с публикации, и микрофон становится невключaемым
        // (ни одной живой публикации с source Microphone не остаётся).
        if (publication.trackSid && publication.trackSid === mixPub.trackSid) return;
        const track = publication.track ?? null;
        if (!track) return;
        if (savedMic.current.track === track) return; // уже наш, не дублируемся
        // Поздно прибывший ВТОРОЙ сырой микрофон (медленный getUserMedia при
        // подключении, тоггл микрофона во время публикации, переподключение).
        // Просто выйти нельзя: он останется опубликован РЯДОМ с миксом —
        // слушатель получит два голосовых трека (двоение голоса и «голос есть,
        // а музыки нет», т.к. слышен он из лишнего трека). Подсаживаем его
        // в шину вместо прежнего голосового источника и снимаем с публикации.
        savedMic.current = { track, enabled: true };
        if (micSource.current) {
          micSource.current.disconnect();
        }
        micSource.current = audioContext.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
        if (voiceVolumeGain.current) {
          micSource.current.connect(voiceVolumeGain.current);
        } else {
          micSource.current.connect(mixLimiter.current ?? mixDest.current);
          if (voiceAnalyser.current) {
            micSource.current.connect(voiceAnalyser.current);
          }
        }
        await localParticipant.unpublishTrack(track, false);
      });
    };
    localParticipant.on(ParticipantEvent.LocalTrackPublished, onLocalTrackPublished);
    return () => {
      localParticipant.off(ParticipantEvent.LocalTrackPublished, onLocalTrackPublished);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- единожды на участника
  }, [localParticipant]);

  // Пинг каждые 10 с — синхронизация часов и RTT для адаптивного запаса старта
  useEffect(() => {
    const first = setTimeout(sendPing, 2000);
    pingIntervalRef.current = setInterval(sendPing, 10000);
    return () => {
      clearTimeout(first);
      clearInterval(pingIntervalRef.current);
    };
  }, [sendPing]);

  // Поздние подключения: новый участник сразу получает состояние, не дожидаясь heartbeat
  const remoteCountRef = useRef(remoteParticipants.length);
  useEffect(() => {
    if (remoteParticipants.length > remoteCountRef.current && lastStartPayloadRef.current) {
      sendSync(lastStartPayloadRef.current);
    }
    remoteCountRef.current = remoteParticipants.length;
  }, [remoteParticipants.length, sendSync]);

  // Громкость «на лету»: ползунок меняет уровень одновременно у ведущего
  // (монитор) и у слушателей (музыка смикширована в общем треке — её уровень
  // задаётся тем же гейном musicBusGain до кодирования)
  useEffect(() => {
    if (currentKind.current !== JUKEBOX_KINDS.music) return;
    if (localGain.current) {
      localGain.current.gain.value = musicVolume;
    }
    if (musicBusGain.current) {
      musicBusGain.current.gain.value = musicVolume;
    }
  }, [musicVolume]);
  useEffect(() => {
    if (currentKind.current !== JUKEBOX_KINDS.metronome) return;
    if (localGain.current) {
      localGain.current.gain.value = metronomeVolume;
    }
    if (musicBusGain.current) {
      musicBusGain.current.gain.value = metronomeVolume;
    }
  }, [metronomeVolume]);

  /**
   * Запускает музыку/метроном в эфир (через постоянную шину).
   * Публикация трека не меняется — старт бесшовный для слушателя.
   * Музыка кодируется в микс на стороне ведущего и уходит слушателю
   * в том же треке, что и голос.
   *
   * @returns {Promise<number|null>} Момент старта звука в audioContext.currentTime.
   */
  const startMusic = useCallback(async (currentTime, source, loop = false, kind = JUKEBOX_KINDS.music, meta = {}) => {
    const serial = ++startSerial.current;
    cleanupMusicInternal();
    const generation = startGeneration.current;
    try {
      // Музыка обязана попасть в ЖИВУЮ опубликованную шину: иначе ведущий
      // слышит локальный монитор, а слушатель — тишину (визуал по data channel
      // при этом честно работает). ensureBus ставит шину, если её нет.
      await ensureBus();
      if (serial !== startSerial.current || generation !== startGeneration.current) return null;
      const busReady = syncMixBus.isActive();
      // Шина не опубликована (установка не удалась, см. консоль): играем
      // ведущему локально, но НЕ транслируем и НЕ шлём визуал — иначе
      // слушатель увидит «играет», а услышит тишину.
      if (!busReady) {
        console.warn('[sync-audio] шина sync-mix не опубликована — музыка только локально');
      }

      await audioContext.resume();
      if (serial !== startSerial.current || generation !== startGeneration.current) return null;

      let audioBuffer;
      if (source instanceof AudioBuffer) {
        audioBuffer = source; // готовый буфер (метроном)
      } else {
        const response = !source ? await fetch(audio.preview) : null;
        const arrayBuffer = !!source ? await new Response(source).arrayBuffer() : await response.arrayBuffer();
        audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      }
      if (serial !== startSerial.current || generation !== startGeneration.current) return null;


      if (serial !== startSerial.current || generation !== startGeneration.current) return null;

      // Запас планирования: сообщение о старте (и визуал слушателя) должны
      // обогнать звук; запас адаптивен под RTT сети
      const delayMs = scheduleDelayMsRef.current;
      const startCtxTime = audioContext.currentTime + delayMs / 1000;

      sourceNode.current = audioContext.createBufferSource();
      sourceNode.current.buffer = audioBuffer;
      sourceNode.current.loop = loop;
      sourceNode.current.start(startCtxTime, currentTime);

      currentKind.current = kind;
      const volumesAtStart = getJukeboxVolumes();
      const initialVolume =
        kind === JUKEBOX_KINDS.metronome
          ? volumesAtStart.metronomeVolume
          : volumesAtStart.musicVolume;
      localGain.current = audioContext.createGain();
      localGain.current.gain.value = initialVolume;
      sourceNode.current.connect(localGain.current);
      localGain.current.connect(audioContext.destination);

      // Музыка в шину микса (микрофон там уже живёт). Через duck-gain и
      // лимитер — голос приглушает музыку, пики не клиппируют вход кодека.
      // Если шина мертва (установка не удалась) — музыка играет только локально.
      if (busReady && (mixLimiter.current || mixDest.current)) {
        musicBusGain.current = audioContext.createGain();
        // Уровень музыки В ЭФИРЕ = ползунку громкости: слушатели слышат ровно то,
        // что ведущий (тот же гейн, что у локального монитора). Тихая музыка
        // должна быть тихой и у собеседника — фиксированная громкость ломала это.
        musicBusGain.current.gain.value = initialVolume;
        sourceNode.current.connect(musicBusGain.current);
        // Ducking: отдельный gain между музыкой и лимитером — автоматизация
        // фейдов на musicBusGain не конфликтует с огибающей ducking'а
        musicDuckGain.current = audioContext.createGain();
        musicDuckGain.current.gain.value = 1;
        musicBusGain.current.connect(musicDuckGain.current);
        musicDuckGain.current.connect(mixLimiter.current ?? mixDest.current);
        // Фейд-ин ~10 мс против щелчка на границе старта.
        // ВАЖНО (кросс-браузерность): якорим автоматизацию в «now», а НЕ в
        // startCtxTime — setValueAtTime, якорённый в будущем, ломает последующий
        // рамп в WebKit (Safari/iOS): gain залипает на 0, ведущий слышит локальный
        // монитор (он без автоматизации), а слушатель получает тишину.
        // Рампа от now достигает 1 ровно к старту звука (startCtxTime ≥ now+0.25).
        const now = audioContext.currentTime;
        musicBusGain.current.gain.setValueAtTime(0, now);
        musicBusGain.current.gain.linearRampToValueAtTime(
          initialVolume,
          Math.max(now + 0.01, startCtxTime + 0.01)
        );
        startDuckingInternal();
      }

      setIsPlayingLocal(true);
      if (busReady) {
        lastStartPayloadRef.current = {
          type: JUKEBOX_SYNC_TOPIC,
          action: 'start',
          kind,
          wallStart: Date.now() + delayMs,
          offset: currentTime,
          bpm: meta.bpm ?? null,
          beatsPerMeasure: meta.beatsPerMeasure ?? null,
          fileName: meta.fileName ?? null,
          scheduleDelayMs: delayMs,
        };
        sendSync(lastStartPayloadRef.current);
        startHeartbeat();
      }

      return startCtxTime;
    } catch (error) {
      console.error('Ошибка при воспроизведении музыки:', error);
      return null;
    }
  }, [audio, audioContext, cleanupMusicInternal, ensureBus, sendSync, startDuckingInternal, startHeartbeat]);

  const stopMusic = useCallback(async () => {
    startGeneration.current += 1;
    setIsPlayingLocal(false);
    stopHeartbeatInternal();
    lastStartPayloadRef.current = null;
    sendSync({ type: JUKEBOX_SYNC_TOPIC, action: 'stop' });
    cleanupMusicInternal();
  }, [cleanupMusicInternal, sendSync, stopHeartbeatInternal]);

  // Размонтирование: не оставляем шину, пинги и heartbeat «сиротами»
  const stopMusicRef = useRef(stopMusic);
  useEffect(() => {
    stopMusicRef.current = stopMusic;
  });

  // Микрофон выключили во время трансляции: звук идёт собеседнику через
  // микрофонный трек, без микрофона он услышит тишину, а визуал и heartbeat
  // продолжали бы убеждать его в обратном — гасим трансляцию.
  useEffect(() => {
    if (isMicrophoneEnabled === false && isPlayingLocal) {
      stopMusicRef.current?.();
    }
  }, [isMicrophoneEnabled, isPlayingLocal]);
  useEffect(() => {
    return () => {
      startGeneration.current += 1;
      stopHeartbeatInternal();
      unpatchMicToggle();
      enqueueBus(async () => {
        await teardownBusInternal();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только размонтирование
  }, []);

  // --- вид состояния для потребителей (перевод часов ведущего в наши) ---
  // nowMs тикает раз в секунду: протухшее по heartbeat состояние гаснет
  // гарантированно, а не при случайном перерендере провайдера
  const [ nowMs, setNowMs ] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remoteSync = useMemo(() => {
    if (!remoteSyncRaw || remoteSyncRaw.from === localParticipant.identity) return null;
    if (!isSyncFresh(remoteSyncRaw, nowMs)) {
      return null; // heartbeat пропал — ведущий, видимо, завершил, а stop не дошёл
    }
    const offset = clockOffsetsRef.current.get(remoteSyncRaw.from) ?? 0;
    return {
      ...remoteSyncRaw,
      wallStart: remoteSyncRaw.wallStart + offset, // часы ведущего → наши часы
    };
  }, [remoteSyncRaw, localParticipant.identity, nowMs]);

  // Сетка битов для калибровщика: доли рисуем по реально слышимым кликам
  useEffect(() => {
    if (remoteSync?.action === 'start' && remoteSync.kind === 'metronome' && remoteSync.bpm > 0) {
      heardBeatCalibrator.setGrid({
        from: remoteSync.from,
        wallStartLocal: remoteSync.wallStart,
        bpm: remoteSync.bpm,
        beatsPerMeasure: remoteSync.beatsPerMeasure,
        initialDelayMs: remoteSync.scheduleDelayMs ?? 250,
      });
    } else {
      heardBeatCalibrator.setGrid(null);
    }
  }, [remoteSync]);

  const jukeBoxKind = remoteSync?.kind ?? JUKEBOX_KINDS.music;
  const jukeBoxParticipant = remoteSync?.from ?? null;

  return (
    <AudioControlsContext.Provider
      value={{
        jukeBoxParticipant,
        jukeBoxKind,
        amIPlayingJukeBox: isPlayingLocal,
        someoneElsePlayingJukeBox: remoteSync?.action === 'start',
        // состояние микрофона: запуск метронома/плеера невозможен без него —
        // звук идёт собеседнику через микрофонный трек
        micEnabled: !!isMicrophoneEnabled,
        remoteSync,
        startMusic,
        stopMusic,
        audio,
        setAudio,
      }}
    >
      {children}
    </AudioControlsContext.Provider>
  );
};

export function useAudioControls() {
  const ctx = useContext(AudioControlsContext);
  if (!ctx) {
    throw new Error("useJukeBox must be used within a WebAudioProvider");
  }
  return ctx;
}
